import { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spinner, Modal, Form } from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl, buildAssetUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminMailingPage.css';
import { logError } from '../../utils/logger';

const AdminMailingInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();
  const editorRef = useRef(null);
  const isUserEditingRef = useRef(false);
  const iconPickerRef = useRef(null);

  const hasPanelMailing = useMemo(() => hasPermission(user, PERMISSION_IDS.PANEL_MAILING), [user]);
  const hasEnviarMails = useMemo(() => hasPermission(user, PERMISSION_IDS.ENVIAR_MAILS), [user]);
  const hasVerMails = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_MAILS), [user]);
  const hasVerDatosUsuarios = useMemo(
    () => hasPermission(user, PERMISSION_IDS.VER_DATOS_USUARIOS_MAILS),
    [user],
  );

  const [activeTab, setActiveTab] = useState('panel');

  const [subject, setSubject] = useState('');
  const [content, setContent] = useState(
    '<h2>¡Hola, [Nombre]!</h2><p>Queríamos contarte de nuestras últimas novedades.</p>',
  );
  const [recipientMode, setRecipientMode] = useState('segment');
  const [recipientUserId, setRecipientUserId] = useState(null);
  const [recipientLabel, setRecipientLabel] = useState('');
  const [recipientSearch, setRecipientSearch] = useState('');
  const [recipientSuggestions, setRecipientSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);

  const [filterType, setFilterType] = useState('all');
  const [filterActivity, setFilterActivity] = useState('all');
  const [filterEmail, setFilterEmail] = useState('');

  const [fontFamily, setFontFamily] = useState("'Open Sans', Arial, sans-serif");
  const [primaryColor, setPrimaryColor] = useState('#1e40af');
  const [textColor, setTextColor] = useState('#111827');
  const [bgColor, setBgColor] = useState('#ffffff');

  const [previewHtml, setPreviewHtml] = useState('');
  const [recipientsCount, setRecipientsCount] = useState(0);
  const [recipients, setRecipients] = useState([]);
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [sending, setSending] = useState(false);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [emailIcons, setEmailIcons] = useState([]);
  const [showIconPicker, setShowIconPicker] = useState(false);
  const [logCopyModal, setLogCopyModal] = useState({ open: false, subject: '', html: '' });
  const [loadingLogCopy, setLoadingLogCopy] = useState(false);

  /** Modal para enlace / imagen / botón (reemplaza prompt nativo) */
  const [insertEditorOpen, setInsertEditorOpen] = useState(false);
  const [insertEditorType, setInsertEditorType] = useState(null);
  const [insertEditorUrl, setInsertEditorUrl] = useState('https://');
  const [insertEditorButtonText, setInsertEditorButtonText] = useState('Ver más');

  useEffect(() => {
    if (user && !hasPanelMailing) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de mailing.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasPanelMailing, navigate, notify]);

  const buildMailFilter = useCallback(() => {
    if (recipientMode === 'single') {
      return recipientUserId ? { recipientUserId } : {};
    }
    return {
      type: filterType,
      activity: filterActivity,
      email: filterEmail.trim(),
    };
  }, [recipientMode, recipientUserId, filterType, filterActivity, filterEmail]);

  const fetchLogs = async () => {
    if (!hasVerMails) return;
    setLoadingLogs(true);
    try {
      const response = await fetch(buildApiUrl('/api/user/mailer/logs'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`Error al cargar logs (${response.status})`);
      const data = await response.json();
      setLogs(data.logs || []);
    } catch (error) {
      logError('[AdminMailingPage] Error fetching logs:', error);
      notify({
        title: 'Error',
        message: error.message || 'Error al cargar el historial',
        variant: 'error',
      });
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    if (hasVerMails && activeTab === 'history') {
      fetchLogs();
    }
  }, [hasVerMails, activeTab]);

  const loadEmailIcons = useCallback(async () => {
    if (!hasEnviarMails || !token) return;
    try {
      const response = await fetch(buildApiUrl('/api/user/mailer/email-icons'), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const data = await response.json();
      setEmailIcons(Array.isArray(data.icons) ? data.icons : []);
    } catch (e) {
      logError('[AdminMailingPage] email-icons:', e);
    }
  }, [hasEnviarMails, token]);

  useEffect(() => {
    loadEmailIcons();
  }, [loadEmailIcons]);

  const generateEmailHTML = (htmlContent, options = {}) => {
    const {
      font = fontFamily,
      bg = bgColor,
      text = textColor,
      primary = primaryColor,
    } = options;

    const style = `
      <style>
        .mail-wrap {
          font-family: ${font};
          background: ${bg};
          color: ${text};
          padding: 24px;
        }
        .mail-card {
          max-width: 720px;
          margin: 0 auto;
          background: #ffffff;
          border-radius: 12px;
          border: 1px solid #e5e7eb;
          overflow: hidden;
        }
        .mail-header {
          background: ${primary};
          color: #fff;
          padding: 16px 20px;
          font-weight: 700;
          font-size: 18px;
        }
        .mail-body {
          padding: 24px;
        }
        .mail-footer {
          padding: 16px 20px;
          border-top: 1px solid #e5e7eb;
          color: #6b7280;
          font-size: 12px;
        }
        .mail-body a {
          color: ${primary};
          text-decoration: none;
        }
        .mail-body a:hover {
          text-decoration: underline;
        }
        .mail-body img {
          max-width: 100%;
          height: auto;
          border-radius: 8px;
        }
        .mail-body h1, .mail-body h2, .mail-body h3 {
          margin: 0 0 8px;
          color: ${text};
        }
        .mail-body p {
          margin: 0 0 10px;
          line-height: 1.6;
        }
        .mail-body ul, .mail-body ol {
          margin: 0 0 12px 18px;
        }
        .mail-body .btn {
          display: inline-block;
          padding: 10px 14px;
          background: ${primary};
          color: #fff;
          border-radius: 8px;
          font-weight: 600;
          text-decoration: none;
          margin: 8px 0;
        }
        .mail-body .btn:hover {
          opacity: 0.9;
        }
      </style>
    `;

    return `<!doctype html>
<html>
<head>
  <meta charset="utf-8">
  ${style}
</head>
<body>
  <div class="mail-wrap">
    <div class="mail-card">
      <div class="mail-header">ALWAYALIGNERS</div>
      <div class="mail-body">${htmlContent}</div>
      <div class="mail-footer">Recibiste este correo porque estás registrado en ALWAYALIGNERS.</div>
    </div>
  </div>
</body>
</html>`;
  };

  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      editorRef.current.innerHTML = content;
    }
  }, []);

  useEffect(() => {
    if (editorRef.current && !isUserEditingRef.current && editorRef.current.innerHTML !== content) {
      editorRef.current.innerHTML = content;
    }
  }, [content]);

  useEffect(() => {
    const html = generateEmailHTML(content, {
      font: fontFamily,
      bg: bgColor,
      text: textColor,
      primary: primaryColor,
    });
    setPreviewHtml(html);
  }, [content, fontFamily, primaryColor, textColor, bgColor]);

  const refreshRecipients = useCallback(async () => {
    if (!hasVerDatosUsuarios || !token) return;
    const filter = buildMailFilter();
    if (recipientMode === 'single' && !filter.recipientUserId) {
      setRecipients([]);
      setRecipientsCount(0);
      return;
    }

    setLoadingRecipients(true);
    try {
      const response = await fetch(buildApiUrl('/api/user/mailer/segment-preview'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ filter }),
      });
      if (!response.ok) {
        throw new Error(`Error al obtener destinatarios (${response.status})`);
      }
      const data = await response.json();
      setRecipients(data.recipients || []);
      setRecipientsCount(data.count || 0);
    } catch (error) {
      logError('[AdminMailingPage] refreshRecipients:', error);
      notify({
        title: 'Error',
        message: error.message || 'No se pudo actualizar la lista de destinatarios',
        variant: 'error',
      });
    } finally {
      setLoadingRecipients(false);
    }
  }, [hasVerDatosUsuarios, token, buildMailFilter, recipientMode, notify]);

  useEffect(() => {
    if (!hasVerDatosUsuarios || activeTab !== 'panel') return;
    const t = setTimeout(() => {
      void refreshRecipients();
    }, 350);
    return () => clearTimeout(t);
  }, [hasVerDatosUsuarios, activeTab, refreshRecipients]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!showIconPicker) return;
      const el = iconPickerRef.current;
      if (el && !el.contains(e.target)) setShowIconPicker(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [showIconPicker]);

  useEffect(() => {
    if (recipientMode !== 'single' || recipientSearch.trim().length < 2) {
      setRecipientSuggestions([]);
      return;
    }
    if (!hasVerDatosUsuarios || !token) return;
    const t = setTimeout(async () => {
      setLoadingSuggestions(true);
      try {
        const q = encodeURIComponent(recipientSearch.trim());
        const response = await fetch(buildApiUrl(`/api/user/mailer/recipient-search?q=${q}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!response.ok) throw new Error('Búsqueda fallida');
        const data = await response.json();
        setRecipientSuggestions(data.users || []);
      } catch (e) {
        setRecipientSuggestions([]);
      } finally {
        setLoadingSuggestions(false);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [recipientSearch, recipientMode, hasVerDatosUsuarios, token]);

  const handleSend = async () => {
    if (!hasEnviarMails) {
      notify({
        title: 'Permiso requerido',
        message: 'No tienes permisos para enviar correos.',
        variant: 'error',
      });
      return;
    }

    if (!subject.trim()) {
      notify({
        title: 'Campo requerido',
        message: 'Por favor ingresa un asunto para el correo.',
        variant: 'warning',
      });
      return;
    }

    if (!content.trim() || content === '<p><br></p>') {
      notify({
        title: 'Campo requerido',
        message: 'Por favor ingresa contenido para el correo.',
        variant: 'warning',
      });
      return;
    }

    const filter = buildMailFilter();
    if (recipientMode === 'single' && !filter.recipientUserId) {
      notify({
        title: 'Destinatario',
        message: 'Selecciona un usuario en la búsqueda o vuelve al modo por segmento.',
        variant: 'warning',
      });
      return;
    }

    const confirmed = window.confirm(
      `¿Enviar este correo a ${recipientsCount} destinatario(s) según los filtros actuales?`,
    );
    if (!confirmed) return;

    setSending(true);
    try {
      const html = generateEmailHTML(content, {
        font: fontFamily,
        bg: bgColor,
        text: textColor,
        primary: primaryColor,
      });

      const response = await fetch(buildApiUrl('/api/user/mailer/segment'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          subject: subject.trim(),
          html,
          filter,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Error al enviar correos (${response.status})`);
      }

      const data = await response.json();
      notify({
        title: 'Correos enviados',
        message: `Se enviaron ${data.count || 0} de ${data.total || 0} correos exitosamente.`,
        variant: 'success',
      });

      setSubject('');
      setContent('<h2>¡Hola, [Nombre]!</h2><p>Queríamos contarte de nuestras últimas novedades.</p>');
      if (hasVerMails) fetchLogs();
      void refreshRecipients();
    } catch (error) {
      logError('[AdminMailingPage] Error sending emails:', error);
      notify({
        title: 'Error',
        message: error.message || 'Error al enviar correos',
        variant: 'error',
      });
    } finally {
      setSending(false);
    }
  };

  const openLogCopy = async (logId) => {
    if (!hasVerMails || !token) return;
    setLoadingLogCopy(true);
    setLogCopyModal({ open: true, subject: '', html: '' });
    try {
      const response = await fetch(buildApiUrl(`/api/user/mailer/logs/${logId}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error('No se pudo cargar la copia');
      const data = await response.json();
      setLogCopyModal({
        open: true,
        subject: data.subject || '(sin asunto)',
        html: data.html || '<p>Sin contenido guardado.</p>',
      });
    } catch (e) {
      notify({ title: 'Error', message: e.message, variant: 'error' });
      setLogCopyModal({ open: false, subject: '', html: '' });
    } finally {
      setLoadingLogCopy(false);
    }
  };

  const applyCommand = (command, value = null) => {
    if (editorRef.current) {
      editorRef.current.focus();
      isUserEditingRef.current = true;
      document.execCommand(command, false, value);
      setContent(editorRef.current.innerHTML);
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
    }
  };

  const closeInsertEditor = useCallback(() => {
    setInsertEditorOpen(false);
    setInsertEditorType(null);
  }, []);

  const openInsertEditor = useCallback((type) => {
    setInsertEditorType(type);
    setInsertEditorUrl('https://');
    setInsertEditorButtonText('Ver más');
    setInsertEditorOpen(true);
  }, []);

  const confirmInsertEditor = useCallback(() => {
    if (!insertEditorType || !editorRef.current) {
      closeInsertEditor();
      return;
    }
    const url = insertEditorUrl.trim();
    const text = insertEditorButtonText.trim() || 'Ver más';

    if (insertEditorType === 'link') {
      if (!url) {
        notify({ title: 'Falta la URL', message: 'Ingresá una dirección para el enlace.', variant: 'error' });
        return;
      }
      editorRef.current.focus();
      isUserEditingRef.current = true;
      document.execCommand('createLink', false, url);
      setContent(editorRef.current.innerHTML);
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
      closeInsertEditor();
      return;
    }

    if (insertEditorType === 'image') {
      if (!url) {
        notify({ title: 'Falta la URL', message: 'Ingresá la dirección de la imagen.', variant: 'error' });
        return;
      }
      editorRef.current.focus();
      isUserEditingRef.current = true;
      try {
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();
        const img = document.createElement('img');
        img.src = url;
        img.style.maxWidth = '100%';
        img.style.borderRadius = '8px';
        img.alt = 'Imagen';
        range.deleteContents();
        range.insertNode(img);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        setContent(editorRef.current.innerHTML);
      } catch (e) {
        document.execCommand('insertImage', false, url);
        setContent(editorRef.current.innerHTML);
      }
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
      closeInsertEditor();
      return;
    }

    if (insertEditorType === 'button') {
      if (!url) {
        notify({ title: 'Falta la URL', message: 'Ingresá el enlace del botón (https://…).', variant: 'error' });
        return;
      }
      editorRef.current.focus();
      isUserEditingRef.current = true;
      try {
        const selection = window.getSelection();
        const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();
        const button = document.createElement('a');
        button.href = url;
        button.className = 'btn';
        button.target = '_blank';
        button.rel = 'noopener noreferrer';
        button.style.cssText =
          'display:inline-block;padding:10px 14px;background:' +
          primaryColor +
          ';color:#fff;border-radius:8px;font-weight:600;text-decoration:none;margin:8px 0;';
        button.textContent = text;
        range.deleteContents();
        range.insertNode(button);
        range.collapse(false);
        selection.removeAllRanges();
        selection.addRange(range);
        setContent(editorRef.current.innerHTML);
      } catch (e) {
        const buttonHtml = `<a href="${url}" class="btn" target="_blank" rel="noopener noreferrer" style="display:inline-block;padding:10px 14px;background:${primaryColor};color:#fff;border-radius:8px;font-weight:600;text-decoration:none;margin:8px 0;">${text}</a>`;
        document.execCommand('insertHTML', false, buttonHtml);
        setContent(editorRef.current.innerHTML);
      }
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
      closeInsertEditor();
    }
  }, [
    insertEditorType,
    insertEditorUrl,
    insertEditorButtonText,
    primaryColor,
    closeInsertEditor,
    notify,
  ]);

  const insertLink = () => openInsertEditor('link');
  const insertImage = () => openInsertEditor('image');
  const insertButton = () => openInsertEditor('button');

  const insertEditorModalMeta = useMemo(() => {
    switch (insertEditorType) {
      case 'link':
        return {
          title: 'Insertar enlace',
          subtitle: 'El texto seleccionado en el editor será el enlace. Si no hay selección, se creará el vínculo en la posición del cursor.',
          icon: 'fa-link',
          urlLabel: 'URL del enlace',
          urlPlaceholder: 'https://…',
        };
      case 'image':
        return {
          title: 'Insertar imagen',
          subtitle: 'La imagen se mostrará desde la URL que indiques (debe ser accesible públicamente).',
          icon: 'fa-image',
          urlLabel: 'URL de la imagen',
          urlPlaceholder: 'https://…',
        };
      case 'button':
        return {
          title: 'Insertar botón',
          subtitle: 'Se inserta un botón con estilo de campaña (color primario del panel).',
          icon: 'fa-square',
          urlLabel: 'URL al hacer clic',
          urlPlaceholder: 'https://…',
        };
      default:
        return {
          title: '',
          subtitle: '',
          icon: 'fa-plus',
          urlLabel: 'URL',
          urlPlaceholder: 'https://',
        };
    }
  }, [insertEditorType]);

  const insertEmailIcon = (fileName) => {
    if (!editorRef.current) return;
    const url = buildAssetUrl(`/email-icons/${fileName}`);
    editorRef.current.focus();
    try {
      const selection = window.getSelection();
      const range = selection.rangeCount > 0 ? selection.getRangeAt(0) : document.createRange();
      const img = document.createElement('img');
      img.src = url;
      img.alt = '';
      img.width = 32;
      img.height = 32;
      img.style.verticalAlign = 'middle';
      img.style.margin = '0 4px';
      range.deleteContents();
      range.insertNode(img);
      range.collapse(false);
      selection.removeAllRanges();
      selection.addRange(range);
      setContent(editorRef.current.innerHTML);
    } catch (e) {
      document.execCommand(
        'insertHTML',
        false,
        `<img src="${url}" alt="" width="32" height="32" style="vertical-align:middle;margin:0 4px" />`,
      );
      setContent(editorRef.current.innerHTML);
    }
    setShowIconPicker(false);
  };

  const handleEditorInput = () => {
    if (editorRef.current) {
      isUserEditingRef.current = true;
      setContent(editorRef.current.innerHTML);
      setTimeout(() => {
        isUserEditingRef.current = false;
      }, 100);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleString('es-ES');
    } catch {
      return dateString;
    }
  };

  const pickRecipient = (u) => {
    setRecipientUserId(u.id);
    setRecipientLabel(`${u.email} — ${u.nombre || ''} ${u.apellido || ''}`.trim());
    setRecipientSearch('');
    setRecipientSuggestions([]);
  };

  const clearSingleRecipient = () => {
    setRecipientUserId(null);
    setRecipientLabel('');
  };

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="mailing"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
            notify({
              title: 'Sección en desarrollo',
              message: 'Estamos migrando esta sección al nuevo panel.',
              variant: 'info',
            });
          }}
        />
        <main className="admin-main">
          <div className="admin-content">
            <div className="page-header mailing-page-header">
              <h1 className="page-title">Mailing</h1>
              <p className="page-subtitle">
                Campañas por segmento o un destinatario, con vista previa de destinatarios e historial
                de envíos.
              </p>
              <div className="mailing-tabs" role="tablist">
                <button
                  type="button"
                  role="tab"
                  aria-selected={activeTab === 'panel'}
                  className={`mailing-tab ${activeTab === 'panel' ? 'is-active' : ''}`}
                  onClick={() => setActiveTab('panel')}
                >
                  Panel de mails
                </button>
                {hasVerMails && (
                  <button
                    type="button"
                    role="tab"
                    aria-selected={activeTab === 'history'}
                    className={`mailing-tab ${activeTab === 'history' ? 'is-active' : ''}`}
                    onClick={() => setActiveTab('history')}
                  >
                    Historial de mails
                  </button>
                )}
              </div>
            </div>

            {activeTab === 'panel' && (
              <div className="mailing-grid">
                <section className="mailing-card mailing-controls">
                  <div className="mailing-card-header">
                    <h2 className="mailing-card-title">Configuración</h2>
                  </div>
                  <div className="mailing-card-body">
                    <div className="mailing-form-group">
                      <label className="mailing-form-label">Asunto</label>
                      <input
                        type="text"
                        className="mailing-form-input"
                        placeholder="Asunto del correo"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                    </div>

                    <div className="mailing-form-group">
                      <label className="mailing-form-label">Destinatarios</label>
                      <div className="mailing-mode-toggle">
                        <button
                          type="button"
                          className={`mailing-mode-btn ${recipientMode === 'segment' ? 'is-active' : ''}`}
                          onClick={() => {
                            setRecipientMode('segment');
                            clearSingleRecipient();
                          }}
                        >
                          Por segmento
                        </button>
                        <button
                          type="button"
                          className={`mailing-mode-btn ${recipientMode === 'single' ? 'is-active' : ''}`}
                          onClick={() => setRecipientMode('single')}
                        >
                          Un solo destinatario
                        </button>
                      </div>
                    </div>

                    {recipientMode === 'single' ? (
                      <div className="mailing-form-group mailing-single-search">
                        <label className="mailing-form-label">Buscar usuario</label>
                        <input
                          type="text"
                          className="mailing-form-input"
                          placeholder="Email o nombre (mín. 2 caracteres)"
                          value={recipientSearch}
                          onChange={(e) => setRecipientSearch(e.target.value)}
                          autoComplete="off"
                        />
                        {loadingSuggestions && (
                          <div className="mailing-suggest-loading">
                            <Spinner size="sm" /> Buscando…
                          </div>
                        )}
                        {recipientSuggestions.length > 0 && (
                          <ul className="mailing-suggest-list">
                            {recipientSuggestions.map((u) => (
                              <li key={u.id}>
                                <button type="button" className="mailing-suggest-item" onClick={() => pickRecipient(u)}>
                                  <span className="mailing-suggest-email">{u.email}</span>
                                  <span className="mailing-suggest-name">
                                    {[u.nombre, u.apellido].filter(Boolean).join(' ') || '—'}
                                  </span>
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                        {recipientUserId && (
                          <div className="mailing-selected-recipient">
                            <span>{recipientLabel}</span>
                            <button type="button" className="mailing-link-btn" onClick={clearSingleRecipient}>
                              Quitar
                            </button>
                          </div>
                        )}
                      </div>
                    ) : (
                      <>
                        <div className="mailing-form-group">
                          <label className="mailing-form-label">Segmentación</label>
                          <select
                            className="mailing-form-select"
                            value={filterType}
                            onChange={(e) => setFilterType(e.target.value)}
                          >
                            <option value="all">Todos</option>
                            <option value="course">Compraron cursos</option>
                            <option value="event">Compraron eventos</option>
                            <option value="item">Compraron ítems (tienda)</option>
                          </select>
                          <span className="mailing-form-help">Basado en adquisiciones del usuario.</span>
                        </div>

                        <div className="mailing-form-row">
                          <div className="mailing-form-group">
                            <label className="mailing-form-label">Actividad</label>
                            <select
                              className="mailing-form-select"
                              value={filterActivity}
                              onChange={(e) => setFilterActivity(e.target.value)}
                            >
                              <option value="all">Todos</option>
                              <option value="withPurchases">Con compras</option>
                              <option value="noPurchases">Sin compras</option>
                            </select>
                          </div>
                          <div className="mailing-form-group">
                            <label className="mailing-form-label">Filtrar por email</label>
                            <input
                              type="text"
                              className="mailing-form-input"
                              placeholder="contiene…"
                              value={filterEmail}
                              onChange={(e) => setFilterEmail(e.target.value)}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    <div className="mailing-form-group">
                      <label className="mailing-form-label">Tipografía</label>
                      <select
                        className="mailing-form-select"
                        value={fontFamily}
                        onChange={(e) => setFontFamily(e.target.value)}
                      >
                        <option value="'Open Sans', Arial, sans-serif">Open Sans</option>
                        <option value="'Inter', Arial, sans-serif">Inter</option>
                        <option value="'Poppins', Arial, sans-serif">Poppins</option>
                        <option value="Arial, sans-serif">Arial</option>
                        <option value="Georgia, serif">Georgia</option>
                      </select>
                    </div>

                    <div className="mailing-form-row-3">
                      <div className="mailing-form-group">
                        <label className="mailing-form-label">Color primario</label>
                        <input
                          type="color"
                          className="mailing-color-input"
                          value={primaryColor}
                          onChange={(e) => setPrimaryColor(e.target.value)}
                        />
                      </div>
                      <div className="mailing-form-group">
                        <label className="mailing-form-label">Color del texto</label>
                        <input
                          type="color"
                          className="mailing-color-input"
                          value={textColor}
                          onChange={(e) => setTextColor(e.target.value)}
                        />
                      </div>
                      <div className="mailing-form-group">
                        <label className="mailing-form-label">Fondo</label>
                        <input
                          type="color"
                          className="mailing-color-input"
                          value={bgColor}
                          onChange={(e) => setBgColor(e.target.value)}
                        />
                      </div>
                    </div>

                    {hasVerDatosUsuarios && (
                      <div className="mailing-recipients-block">
                        <div className="mailing-recipients-head">
                          <span className="mailing-form-label" style={{ marginBottom: 0 }}>
                            Destinatarios ({recipientsCount})
                          </span>
                          <button
                            type="button"
                            className="mailing-link-btn"
                            onClick={() => void refreshRecipients()}
                            disabled={loadingRecipients}
                          >
                            {loadingRecipients ? 'Actualizando…' : 'Actualizar lista'}
                          </button>
                        </div>
                        <div className="mailing-recipients-scroll">
                          {loadingRecipients && recipients.length === 0 ? (
                            <div className="mailing-empty-state">
                              <Spinner size="sm" /> Cargando…
                            </div>
                          ) : recipients.length === 0 ? (
                            <div className="mailing-empty-state">
                              {recipientMode === 'single' && !recipientUserId
                                ? 'Elige un usuario arriba para ver a quién se enviará.'
                                : 'Ningún destinatario coincide con los filtros.'}
                            </div>
                          ) : (
                            <table className="mailing-recipients-table">
                              <thead>
                                <tr>
                                  <th>Email</th>
                                  <th>Nombre</th>
                                </tr>
                              </thead>
                              <tbody>
                                {recipients.map((r) => (
                                  <tr key={r.id}>
                                    <td>{r.email}</td>
                                    <td>
                                      {[r.nombre, r.apellido].filter(Boolean).join(' ') || '—'}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mailing-actions">
                      <button
                        type="button"
                        className="mailing-btn mailing-btn-secondary"
                        onClick={() => setShowPreviewModal(true)}
                      >
                        <i className="fas fa-eye" aria-hidden="true" />
                        <span>Previsualizar correo</span>
                      </button>
                      {hasEnviarMails && (
                        <button
                          type="button"
                          className="mailing-btn mailing-btn-primary"
                          onClick={handleSend}
                          disabled={sending}
                        >
                          {sending ? (
                            <>
                              <Spinner size="sm" />
                              <span>Enviando…</span>
                            </>
                          ) : (
                            <>
                              <i className="fas fa-paper-plane" aria-hidden="true" />
                              <span>Enviar</span>
                            </>
                          )}
                        </button>
                      )}
                    </div>
                  </div>
                </section>

                <section className="mailing-card mailing-editor">
                  <div className="mailing-editor-header">
                    <p className="mailing-editor-header-text">
                      Editor del cuerpo del mensaje. Los iconos SVG se insertan como imagen enlazada al
                      servidor (visibles en clientes de correo que permitan imágenes externas).
                    </p>
                  </div>
                  <div className="mailing-editor-wrapper">
                    <div className="mailing-toolbar">
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('formatBlock', 'h1')}
                        title="Título 1"
                      >
                        <i className="fas fa-heading" aria-hidden="true" />
                        <span>H1</span>
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('formatBlock', 'h2')}
                        title="Título 2"
                      >
                        <i className="fas fa-heading" aria-hidden="true" />
                        <span>H2</span>
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('formatBlock', 'h3')}
                        title="Título 3"
                      >
                        <i className="fas fa-heading" aria-hidden="true" />
                        <span>H3</span>
                      </button>
                      <div className="mailing-tool-separator" />
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('bold')}
                        title="Negrita"
                      >
                        <i className="fas fa-bold" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('italic')}
                        title="Cursiva"
                      >
                        <i className="fas fa-italic" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('underline')}
                        title="Subrayado"
                      >
                        <i className="fas fa-underline" aria-hidden="true" />
                      </button>
                      <div className="mailing-tool-separator" />
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('insertUnorderedList')}
                        title="Lista con viñetas"
                      >
                        <i className="fas fa-list-ul" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('insertOrderedList')}
                        title="Lista numerada"
                      >
                        <i className="fas fa-list-ol" aria-hidden="true" />
                      </button>
                      <div className="mailing-tool-separator" />
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('justifyLeft')}
                        title="Alinear izquierda"
                      >
                        <i className="fas fa-align-left" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('justifyCenter')}
                        title="Centrar"
                      >
                        <i className="fas fa-align-center" aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('justifyRight')}
                        title="Alinear derecha"
                      >
                        <i className="fas fa-align-right" aria-hidden="true" />
                      </button>
                      <div className="mailing-tool-separator" />
                      <button type="button" className="mailing-tool-btn" onClick={insertLink} title="Enlace">
                        <i className="fas fa-link" aria-hidden="true" />
                      </button>
                      <button type="button" className="mailing-tool-btn" onClick={insertImage} title="Imagen">
                        <i className="fas fa-image" aria-hidden="true" />
                      </button>
                      {hasEnviarMails && emailIcons.length > 0 && (
                        <div className="mailing-tool-picker-wrap" ref={iconPickerRef}>
                          <button
                            type="button"
                            className="mailing-tool-btn"
                            title="Icono SVG"
                            onClick={() => setShowIconPicker((v) => !v)}
                          >
                            <i className="far fa-image" aria-hidden="true" />
                            <span>Icono</span>
                          </button>
                          {showIconPicker && (
                            <div className="mailing-icon-picker">
                              {emailIcons.map((name) => (
                                <button
                                  key={name}
                                  type="button"
                                  className="mailing-icon-picker-cell"
                                  title={name}
                                  onClick={() => insertEmailIcon(name)}
                                >
                                  <img src={buildAssetUrl(`/email-icons/${name}`)} alt="" width={28} height={28} />
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      <button type="button" className="mailing-tool-btn" onClick={insertButton} title="Botón">
                        <i className="fas fa-square" aria-hidden="true" />
                        <span>Botón</span>
                      </button>
                      <div className="mailing-tool-separator" />
                      <button
                        type="button"
                        className="mailing-tool-btn"
                        onClick={() => applyCommand('removeFormat')}
                        title="Limpiar formato"
                      >
                        <i className="fas fa-eraser" aria-hidden="true" />
                      </button>
                    </div>
                    <div
                      ref={editorRef}
                      className="mailing-editor-content"
                      contentEditable
                      role="textbox"
                      aria-multiline="true"
                      onInput={handleEditorInput}
                      suppressContentEditableWarning
                    />
                  </div>
                </section>
              </div>
            )}

            {activeTab === 'history' && hasVerMails && (
              <section className="mailing-card mailing-history-card">
                <div className="mailing-logs-header">
                  <h2 className="mailing-logs-title">Historial de envíos</h2>
                  <button
                    type="button"
                    className="mailing-btn mailing-btn-secondary mailing-btn-inline"
                    onClick={() => void fetchLogs()}
                    disabled={loadingLogs}
                  >
                    {loadingLogs ? (
                      <>
                        <Spinner size="sm" />
                        <span>Cargando…</span>
                      </>
                    ) : (
                      <>
                        <i className="fas fa-sync-alt" aria-hidden="true" />
                        <span>Actualizar</span>
                      </>
                    )}
                  </button>
                </div>
                <div className="mailing-card-body mailing-history-body">
                  {loadingLogs ? (
                    <div className="mailing-empty-state">
                      <Spinner />
                    </div>
                  ) : logs.length === 0 ? (
                    <div className="mailing-empty-state">No hay correos enviados aún.</div>
                  ) : (
                    <div className="mailing-table-wrapper">
                      <table className="mailing-table">
                        <thead>
                          <tr>
                            <th>Fecha</th>
                            <th>Asunto</th>
                            <th>Enviados</th>
                            <th />
                          </tr>
                        </thead>
                        <tbody>
                          {logs.map((log) => {
                            const recipientsList = Array.isArray(log.recipients)
                              ? log.recipients
                              : (() => {
                                  try {
                                    return JSON.parse(log.recipients || '[]');
                                  } catch {
                                    return [];
                                  }
                                })();
                            return (
                              <tr key={log.id}>
                                <td>{formatDate(log.created_at)}</td>
                                <td>{log.subject}</td>
                                <td>
                                  {log.sent_count || 0} / {recipientsList.length}
                                </td>
                                <td className="mailing-table-actions">
                                  <button
                                    type="button"
                                    className="mailing-link-btn"
                                    onClick={() => void openLogCopy(log.id)}
                                  >
                                    Ver copia
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>
        </main>

        <Modal
          show={insertEditorOpen && Boolean(insertEditorType)}
          onHide={closeInsertEditor}
          centered
          backdrop="static"
          contentClassName="mailing-modal-content mailing-insert-editor-modal"
          dialogClassName="mailing-insert-editor-dialog"
          enforceFocus={false}
        >
          <div className="mailing-modal-header mailing-insert-editor-header">
            <div className="mailing-insert-editor-head-row">
              <span className="mailing-insert-editor-icon" aria-hidden>
                <i className={`fas ${insertEditorModalMeta.icon}`} />
              </span>
              <div>
                <h3 className="mailing-modal-title mb-0">{insertEditorModalMeta.title}</h3>
                {insertEditorModalMeta.subtitle && (
                  <p className="mailing-insert-editor-sub text-muted small mb-0 mt-1">
                    {insertEditorModalMeta.subtitle}
                  </p>
                )}
              </div>
            </div>
            <button
              type="button"
              className="mailing-modal-close"
              onClick={closeInsertEditor}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          <div className="mailing-modal-body mailing-insert-editor-body">
            {insertEditorType === 'button' && (
              <Form.Group className="mb-3" controlId="mailing-insert-btn-text">
                <Form.Label className="mailing-form-label">Texto del botón</Form.Label>
                <Form.Control
                  type="text"
                  value={insertEditorButtonText}
                  onChange={(e) => setInsertEditorButtonText(e.target.value)}
                  placeholder="Ej. Ver más, Comprar ahora"
                  autoFocus
                />
              </Form.Group>
            )}
            <Form.Group className="mb-0" controlId="mailing-insert-url">
              <Form.Label className="mailing-form-label">{insertEditorModalMeta.urlLabel}</Form.Label>
              <Form.Control
                type="url"
                value={insertEditorUrl}
                onChange={(e) => setInsertEditorUrl(e.target.value)}
                placeholder={insertEditorModalMeta.urlPlaceholder}
                autoFocus={insertEditorType !== 'button'}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    confirmInsertEditor();
                  }
                }}
              />
            </Form.Group>
          </div>
          <div className="mailing-modal-footer mailing-insert-editor-footer">
            <button type="button" className="mailing-btn mailing-btn-secondary" onClick={closeInsertEditor}>
              Cancelar
            </button>
            <button type="button" className="mailing-btn mailing-btn-primary" onClick={confirmInsertEditor}>
              Insertar
            </button>
          </div>
        </Modal>

        <Modal
          show={showPreviewModal}
          onHide={() => setShowPreviewModal(false)}
          size="lg"
          contentClassName="mailing-modal-content"
        >
          <div className="mailing-modal-header">
            <h3 className="mailing-modal-title">Previsualización del correo</h3>
            <button
              type="button"
              className="mailing-modal-close"
              onClick={() => setShowPreviewModal(false)}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          <div className="mailing-modal-body">
            <div className="mailing-preview-frame-wrap">
              <iframe title="Vista previa" className="mailing-history-iframe" srcDoc={previewHtml} sandbox="" />
            </div>
          </div>
          <div className="mailing-modal-footer">
            <button type="button" className="mailing-btn mailing-btn-secondary" onClick={() => setShowPreviewModal(false)}>
              Cerrar
            </button>
          </div>
        </Modal>

        <Modal
          show={logCopyModal.open}
          onHide={() => setLogCopyModal({ open: false, subject: '', html: '' })}
          size="xl"
          contentClassName="mailing-modal-content"
        >
          <div className="mailing-modal-header">
            <h3 className="mailing-modal-title">Copia guardada — {logCopyModal.subject}</h3>
            <button
              type="button"
              className="mailing-modal-close"
              onClick={() => setLogCopyModal({ open: false, subject: '', html: '' })}
              aria-label="Cerrar"
            >
              ×
            </button>
          </div>
          <div className="mailing-modal-body">
            {loadingLogCopy ? (
              <div className="mailing-empty-state">
                <Spinner />
              </div>
            ) : (
              <iframe
                title="Copia del envío"
                className="mailing-history-iframe mailing-history-iframe-tall"
                srcDoc={logCopyModal.html}
                sandbox=""
              />
            )}
          </div>
          <div className="mailing-modal-footer">
            <button
              type="button"
              className="mailing-btn mailing-btn-secondary"
              onClick={() => setLogCopyModal({ open: false, subject: '', html: '' })}
            >
              Cerrar
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
};

const AdminMailingPage = () => (
  <AdminNotificationsProvider>
    <AdminMailingInner />
  </AdminNotificationsProvider>
);

export default AdminMailingPage;
